import React, { ReactElement, useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useApiRequest } from 'src/hooks/useApi';
import { useAppToolchain } from 'src/hooks/useAppToolchain';
import useAuth from 'src/hooks/useAuth';
import LoadingIndicator from 'src/lib/components/LoadingIndicator';
import { CodeusAccountConfig, DIConnector } from 'src/types/dataTypes';

export function withRequireOnboarding(
	Component: (props) => ReactElement,
): (props) => ReactElement {
	const [doCompleteOnboarding, setDoCompleteOnboarding] = useState(false);
	const [reloadConfig, setReloadConfig] = useState(0);
	const [page, setPage] = useState('loading');
	const { auth } = useAuth();
	const { api } = useAppToolchain();
	const isSetup = localStorage.getItem('is_setup') == 'true';

	if (isSetup) {
		const SetupComponent = () => <Navigate to="/welcome" />;
		SetupComponent.displayName = 'WithRequireOnboardingSetup';
		return SetupComponent;
	}

	const [connectorStatus, connectors] = useApiRequest<DIConnector[]>(
		auth &&
			(() => api.getAccountConnectors(auth.accountDetails.account.id)),
		[auth, api],
	);

	const [configStatus, config] = useApiRequest<CodeusAccountConfig>(() => {
		if (!auth) return;
		return api.getAccountConfig(auth?.accountDetails.account.id);
	}, [auth, api, reloadConfig]);

	const [assignStatus, assignments] = useApiRequest(() => {
		if (!auth) return;
		return api.getAccountAssignment(auth.accountDetails.account.id, {
			obj: 'obj_all',
			operation: 'delete',
		});
	}, [auth, api]);

	useApiRequest(
		doCompleteOnboarding &&
			(() =>
				api.saveAccountConfig(auth.accountDetails.account.id, [
					{
						path: '/onboardedTime',
						op: 'replace',
						value: new Date().toISOString(),
					},
				])),
		[doCompleteOnboarding],
		() => {
			setReloadConfig(reloadConfig + 1);
		},
	);

	useEffect(() => {
		if (assignStatus == 'REJECTED' || configStatus == 'REJECTED') {
			setPage('requestedPage');
		} else {
			if (config && assignments && connectors) {
				if (config.onboardedTime == null) {
					if (connectors.length > 0) {
						setDoCompleteOnboarding(true);
						setPage('loading');
					} else {
						let isWidgetAllowed = false;
						const currentUserId = auth.user.id;
						assignments.data.forEach((ass) => {
							if (ass.subject == currentUserId) {
								isWidgetAllowed = true;
							}
						});
						if (isWidgetAllowed) {
							setPage('widget');
						} else {
							setPage('requestedPage');
						}
					}
				} else {
					setPage('requestedPage');
				}
			}
		}
	}, [auth, assignments, connectors, config, assignStatus]);

	if (page == 'loading') {
		const LoadingComponent = () => <LoadingIndicator />;
		LoadingComponent.displayName = 'WithRequireOnboardingLoading';
		return LoadingComponent;
	} else if (page == 'widget') {
		const WidgetComponent = () => <Navigate to="/welcome" />;
		WidgetComponent.displayName = 'WithRequireOnboardingWidget';
		return WidgetComponent;
	} else {
		const WrappedComponent = (topProps) => {
			return <Component {...topProps} />;
		};
		WrappedComponent.displayName = 'WithRequireOnboardingWrapped';
		return WrappedComponent;
	}
}
